// DCF model applicability (StockApp docs/dcf-financials-plan.md, 2026-09-12).
// The pipeline publishes flagged tickers the FCF-based DCF does not apply to
// (financials: OCF carries float/deposits; non-USD reporters) as SEPARATE
// arrays: dcf_sp500_not_applicable.json / dcf_nasdaq_not_applicable.json.
// The main DCF arrays contain applicable valuations only, so old builds
// simply see fewer rows. Pure helpers, tested without React.

export const NOT_APPLICABLE_KEYS = {
  sp500: 'dcf_sp500_not_applicable.json',
  nasdaq: 'dcf_nasdaq_not_applicable.json',
};

export const REASON_LABEL = {
  financial_sector: 'Financials',
  real_estate_sector: 'Real estate',
  non_usd_reporter: 'Non-USD reporter',
  // StockApp decision 19 (2026-09-15): regulated utilities, and the two
  // "no valuation" outcomes that used to vanish from the table entirely.
  regulated_utility: 'Regulated utility',
  fcf_nonpositive: 'No trailing free cash flow',
  // decision 20: a model output, not a verdict on the company
  equity_below_debt: 'Model EV below debt',
  captive_finance: 'Captive finance arm',
};

export const FALLBACK_REASON_TEXT = {
  financial_sector:
    'FCF-based DCF is unreliable for financials: operating cash flow includes float, ' +
    'deposits and lending flows that are not distributable free cash flow.',
  real_estate_sector:
    'FCF-based DCF is unreliable for REITs and real-estate operators: property acquisitions ' +
    'sit inside capex and operating cash flow is not FFO, so the model misprices the sector ' +
    'in both directions.',
  non_usd_reporter:
    "Reports in a non-USD currency; the model's inputs are not in USD, so the " +
    'valuation is not comparable to the quote.',
  regulated_utility:
    'FCF-based DCF is unreliable for regulated utilities: capex is rate-base investment ' +
    'funded by debt and equity, so operating cash flow minus capex is negative by design ' +
    'and carries no valuation signal.',
  fcf_nonpositive:
    'No valuation: trailing free cash flow (operating cash flow minus capex over the last ' +
    'four quarters) is not positive, so there is nothing to project.',
  equity_below_debt:
    "No valuation: the model's enterprise value is below the debt it subtracts, so the " +
    'modelled equity is not positive. This is a model output, not a solvency verdict.',
  captive_finance:
    'FCF-based DCF is unreliable for companies with a captive finance arm: the consolidated ' +
    'debt funds customer lending and is matched by finance receivables the model does not ' +
    'see, so subtracting it misstates the equity.',
};

/** Normalise one published row; unknown reasons still render. */
export function normaliseNotApplicable(row) {
  if (!row || !row.ticker) return null;
  const reason = row.reason || 'unknown';
  return {
    ticker: String(row.ticker).toUpperCase(),
    sector: row.sector || null,
    reason,
    label: REASON_LABEL[reason] || 'Model not applicable',
    text: row.reason_text || FALLBACK_REASON_TEXT[reason] || 'The DCF model does not apply to this ticker.',
    currentPrice: row.current_price != null && !Number.isNaN(parseFloat(row.current_price))
      ? parseFloat(row.current_price) : null,
  };
}

/** Sorted, de-duplicated list from a published array (tolerates non-arrays). */
export function notApplicableRows(payload) {
  const list = Array.isArray(payload) ? payload : [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const row = normaliseNotApplicable(raw);
    if (!row || seen.has(row.ticker)) continue;
    seen.add(row.ticker);
    out.push(row);
  }
  return out.sort((a, b) => a.ticker.localeCompare(b.ticker));
}

/** Rows matching the DCF tab's search box (ticker or sector). */
export function filterNotApplicable(rows, searchTerm) {
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) return rows;
  return rows.filter(r => r.ticker.toLowerCase().includes(term)
    || (r.sector || '').toLowerCase().includes(term));
}

/** Counts per reason for the tiles / footer. */
export function countByReason(rows) {
  return rows.reduce((acc, r) => ({ ...acc, [r.reason]: (acc[r.reason] || 0) + 1 }), {});
}
